import React, { useState, useEffect } from "react";
import Fuse from "fuse.js";
import faqs from "./faqs.json";
import "./ChatBot.css";
import francoImg from "./assets/franco.jpeg";
import { v4 as uuidv4 } from "uuid";

// Sinonimi per migliorare il matching
const synonyms = {
  segreteria: ["ufficio studenti", "ufficio iscrizioni", "student office"],
  tessera: ["card", "abbonamento", "badge"],
  iscrizione: ["immatricolazione", "registrazione", "enrollment"],
  esami: ["appelli", "prove", "test"],
  cfu: ["crediti", "crediti formativi"],
  lezioni: ["corsi", "insegnamenti", "classi"],
  mensa: ["ristorante universitario", "mensa studenti"],
  biblioteca: ["library", "aula studio"],
  trasporti: ["bus", "autobus", "pullman"],
};

function preprocessInput(input) {
  let processed = input.toLowerCase();
  Object.entries(synonyms).forEach(([key, values]) => {
    values.forEach((v) => {
      if (processed.includes(v)) processed = processed.replace(v, key);
    });
  });
  return processed;
}

const fuseOptions = {
  includeScore: true,
  threshold: 0.3,
  keys: ["question", "aliases", "tags"],
};

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [fuse, setFuse] = useState(null);
  const [userId] = useState(() => uuidv4());
  const [typing, setTyping] = useState(false); // stato typing

  const randomSuggestions = () => {
    const shuffled = [...faqs].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3).map((f) => f.question);
  };

  useEffect(() => {
    const f = new Fuse(faqs, fuseOptions);
    setFuse(f);

    setMessages([
      {
        sender: "bot",
        text: "Ciao 👋 sono il bot CusAs! Puoi chiedermi cose come:",
        suggestions: randomSuggestions(),
      },
    ]);
  }, []);

  const handleSend = async (text) => {
    if (!text.trim()) return;

    setMessages((prev) => [...prev, { sender: "user", text }]);
    const processedInput = preprocessInput(text);

    let answered = false;

    // --- Risposta da Fuse.js ---
    if (fuse) {
      const results = fuse.search(processedInput);
      if (results.length > 0 && results[0].score <= fuseOptions.threshold) {
        const botText = results[0].item.answer;
        const botSuggestions = results.slice(1).map((r) => r.item.question);
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: botText, suggestions: botSuggestions },
        ]);
        answered = true;
      }
    }

    // --- Fallback Dialogflow ---
    if (!answered) {
      try {
        setTyping(true); // mostra typing
        const res = await fetch("https://cusaswebapp.onrender.com/dialogflow", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: text, userId }),
        });

        if (!res.ok) throw new Error("Errore nella richiesta al server");

        const data = await res.json();
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: data.reply || "🤖 Non ho capito bene 😅",
            suggestions: randomSuggestions(),
          },
        ]);
      } catch (err) {
        console.error("Dialogflow fetch error:", err);
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "Errore nel contattare il bot 😢",
            suggestions: randomSuggestions(),
          },
        ]);
      } finally {
        setTyping(false); // nascondi typing
      }
    }

    setInput("");
  };

  const handleSuggestionClick = (s) => handleSend(s);

  return (
    <div className="chatbot-container">
      <h2>ChatBot CUSAS</h2>

      <div className="chatbot-messages">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`message-row ${msg.sender === "bot" ? "bot" : "user"}`}
          >
            {msg.sender === "bot" && (
              <img src={francoImg} alt="Bot" className="bot-icon" />
            )}
            <div
              className="message-bubble"
              dangerouslySetInnerHTML={{ __html: msg.text }}
            />
            {msg.sender === "bot" &&
              msg.suggestions &&
              msg.suggestions.length > 0 && (
                <div className="suggestions">
                  {msg.suggestions.map((s, i) => (
                    <button
                      key={i}
                      className="suggestion-button"
                      onClick={() => handleSuggestionClick(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
          </div>
        ))}

        {typing && (
          <div className="message-row bot">
            <img src={francoImg} alt="Bot" className="bot-icon" />
            <div className="message-bubble">🤖 Sta scrivendo...</div>
          </div>
        )}
      </div>

      <div className="chatbot-input">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend(input)}
          placeholder="Scrivi qui la tua domanda..."
        />
        <button onClick={() => handleSend(input)}>Invia</button>
      </div>
    </div>
  );
};

export default ChatBot;
